import {IconButton} from '@dropbox/dig-components/buttons';
import {Text} from '@dropbox/dig-components/typography';
// import {Tooltip} from '@dropbox/dig-components/tooltips';
import {Box} from '@dropbox/dig-foundations';
import {UIIcon} from '@dropbox/dig-icons';
import {CheckmarkCircleFill, CopyLine} from '@dropbox/dig-icons/assets';
import {t} from 'i18next';
import {CSSProperties, useCallback, useState} from 'react';

// Monospace stack + code sizing that DIG Box props don't model; kept inline so
// the component stays self-contained (no CSS module).
const CODE_FONT = "ui-monospace, 'SF Mono', Menlo, Monaco, Consolas, monospace";

/**
 * Borderless icon button that copies `value` to the clipboard and briefly
 * swaps to a checkmark for feedback. `label` describes what is being copied
 * (used for the accessible name); `onCopy` lets callers log analytics.
 */
export const CopyButton = ({
  value,
  label,
  onCopy,
}: {
  value: string;
  label: string;
  onCopy?: () => void;
}) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(() => {
    void navigator.clipboard.writeText(value).then(() => {
      setCopied(true);
      onCopy?.();
      setTimeout(() => setCopied(false), 2000);
    });
  }, [value, onCopy]);

  return (
    <IconButton variant="borderless" onClick={handleCopy} aria-label={t('copy_label', {label})}>
      <UIIcon src={copied ? CheckmarkCircleFill : CopyLine} />
    </IconButton>
  );
};

/**
 * Monospace, code-styled block with an inline copy button in the top-right.
 * Pass `wrap` for prose (e.g. a prompt) that should soft-wrap instead of
 * scrolling horizontally.
 */
export const CopyBlock = ({
  code,
  label,
  wrap = false,
  onCopy,
}: {
  code: string;
  label: string;
  wrap?: boolean;
  onCopy?: () => void;
}) => {
  const codeStyle: CSSProperties = {
    fontFamily: CODE_FONT,
    ...(wrap
      ? {whiteSpace: 'pre-wrap', wordBreak: 'break-word', paddingRight: 48}
      : {whiteSpace: 'pre', overflowX: 'auto'}),
  };

  return (
    <Box position="relative">
      <Text size="small" color="subtle">
        <Box
          padding="16"
          borderRadius="Medium"
          borderStyle="Solid"
          borderColor="Border Subtle"
          borderWidth="1"
          backgroundColor="Background Subtle"
          margin="0"
          style={codeStyle}
        >
          {code}
        </Box>
      </Text>
      {/* <Tooltip title={label} placement="right" openDelay={0}> */}
      <Box position="absolute" style={{top: 8, right: 8}}>
        <CopyButton value={code} label={label} onCopy={onCopy} />
      </Box>
      {/* </Tooltip> */}
    </Box>
  );
};
